import { CheckCircle, ErrorOutline, Schedule } from "@mui/icons-material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import React from "react";
import { useCelleraTheme } from "theme/useCelleraTheme";

import CustomTable, { CustomTableColumn } from "../../common/CustomTable";

const PAGE_SIZE_OPTIONS: number[] = [10, 25, 50];

export enum TransactionStatus {
  PAID = "paid",
  FAILED = "failed",
  PENDING = "pending",
}

interface Transaction {
  id: string;
  datetime: string;
  item: string;
  amount: number;
  currency?: string;
  status: string;
  pdfUrl?: string;
}

interface BillingHistoryTableProps {
  transactions: Transaction[];
  loading?: boolean;
  page: number;
  pageSize: number;
  totalCount: number;
  onPageSizeChange: (pageSize: number) => void;
  onViewInvoice: (pdfUrl: string) => void;
}

const BillingHistoryTable: React.FC<BillingHistoryTableProps> = ({
  transactions,
  loading = false,
  page,
  pageSize,
  totalCount,
  onPageSizeChange,
  onViewInvoice,
}) => {
  const { colors } = useCelleraTheme();

  const formatDate = (dateString: string) => {
    try {
      return dayjs(dateString).format("MMM DD, YYYY");
    } catch {
      return dateString;
    }
  };

  const formatAmount = (amount: number, currency?: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: (currency || "USD").toUpperCase(),
    }).format(amount / 100);
  };

  const getStatusDisplay = (status: string) => {
    switch (status) {
      case TransactionStatus.PAID:
        return {
          icon: CheckCircle,
          backgroundColor: colors.success[100],
          color: colors.success[700],
        };
      case TransactionStatus.FAILED:
        return {
          icon: ErrorOutline,
          backgroundColor: colors.error[100],
          color: colors.error[700],
        };
      case TransactionStatus.PENDING:
      default:
        return {
          icon: Schedule,
          backgroundColor: colors.warning[100],
          color: colors.warning[700],
        };
    }
  };

  const columns: CustomTableColumn<Transaction>[] = [
    {
      id: "datetime",
      label: "DateTime",
      getValue: (row) => formatDate(row.datetime),
      sortable: true,
      sortAccessor: (row) => row.datetime,
      width: 120,
    },
    {
      id: "item",
      label: "Item",
      getValue: (row) => (
        <Box
          sx={{
            maxWidth: 300,
            wordWrap: "break-word",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            lineHeight: 1.4,
          }}
        >
          {row.item}
        </Box>
      ),
      sortable: true,
      width: 300,
    },
    {
      id: "amount",
      label: "Amount",
      getValue: (row) => formatAmount(row.amount, row.currency),
      align: "right",
      sortable: true,
      sortAccessor: (row) => row.amount,
      width: 100,
    },
    {
      id: "status",
      label: "Status",
      getValue: (row) => {
        const statusDisplay = getStatusDisplay(row.status);
        const StatusIcon = statusDisplay.icon;

        return (
          <Chip
            icon={<StatusIcon sx={{ fontSize: "14px !important" }} />}
            label={row.status.toUpperCase()}
            size="small"
            sx={{
              backgroundColor: statusDisplay.backgroundColor,
              color: statusDisplay.color,
              fontWeight: 600,
              fontSize: "0.75rem",
              "& .MuiChip-icon": {
                color: statusDisplay.color,
              },
            }}
          />
        );
      },
      align: "center",
      sortable: true,
      width: 100,
    },
    {
      id: "actions",
      label: "Actions",
      getValue: (row) =>
        row.pdfUrl ? (
          <IconButton
            onClick={() => onViewInvoice(row.pdfUrl || "")}
            aria-label="view invoice"
          >
            <VisibilityOutlinedIcon />
          </IconButton>
        ) : (
          <Typography variant="body2" color="text.secondary">
            -
          </Typography>
        ),
      align: "center",
      width: 30,
    },
  ];

  return (
    <Card sx={{ p: 0 }}>
      <CardContent sx={{ p: 0 }}>
        <CustomTable
          data={transactions}
          columns={columns}
          page={page}
          rowsPerPage={pageSize}
          rowsPerPageOptions={PAGE_SIZE_OPTIONS}
          totalCount={totalCount}
          onPageChange={() => {
            // Placeholder for page change logic
          }}
          onRowsPerPageChange={(event) =>
            onPageSizeChange(Number(event.target.value))
          }
          isLoading={loading}
          getRowKey={(row) => row.id}
          tableContainerSx={{
            overflowX: "auto",
            "&::-webkit-scrollbar": {
              height: 8,
              width: 8,
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: colors.background.secondary,
              borderRadius: 4,
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: colors.border.main,
              borderRadius: 4,
              "&:hover": {
                backgroundColor: colors.border.dark,
              },
            },
            "@media (max-width: 768px)": {
              "& .MuiTableCell-root": {
                padding: "8px 4px",
                fontSize: "0.75rem",
              },
              "& .MuiTableSortLabel-root": {
                fontSize: "0.75rem",
              },
            },
          }}
          paperSx={{
            borderRadius: { xs: 2, sm: 3 },
          }}
        />
      </CardContent>
    </Card>
  );
};

export default BillingHistoryTable;
